import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { HttpEventType, HttpUploadProgressEvent } from '@angular/common/http';
import { ObjectStorageRepository } from '../../infrastructure/http/repositories/object-storage.repository';
import envConfig from '../../env-config';

export interface UploadProgress {
  percent: number;
  done: boolean;
}

@Injectable({ providedIn: 'root' })
export class ObjectStorageService {
  private readonly repo = inject(ObjectStorageRepository);

  /**
   * Sanitize a filename to match legacy fps-cp behavior:
   * 1. Strip OS-invalid file chars
   * 2. Strip non-ASCII chars (mirrors FilenameSanitationRegEx = [^\x00-\x7F])
   */
  sanitizeFileName(fileName: string): string {
    const lastDot = fileName.lastIndexOf('.');
    const baseName = lastDot > 0 ? fileName.substring(0, lastDot) : fileName;
    const ext = lastDot > 0 ? fileName.substring(lastDot) : '';

    // Remove OS-invalid file characters
    let sanitized = baseName.replace(/[\\/:*?"<>|]/g, '');
    // Remove non-ASCII characters (mirrors FilenameSanitationRegEx = [^\x00-\x7F] in legacy config)
    // eslint-disable-next-line no-control-regex
    sanitized = sanitized.replace(/[^\x00-\x7F]/g, '').trim();

    return sanitized + ext;
  }

  /**
   * Temp path pattern: {UploadFolderPath}/{ArNumber}/{RequestGuid}/{SanitizedFileName}
   */
  buildTempPath(
    arNumber: string,
    requestGuid: string,
    fileName: string
  ): string {
    const uploadFolderPath = envConfig.uploadFolderPath ?? 'DEV/UploadedFiles';
    const sanitized = this.sanitizeFileName(fileName);
    return [uploadFolderPath, arNumber, requestGuid.toUpperCase(), sanitized]
      .filter(segment => segment.length > 0)
      .join('/');
  }

  /**
   * The session directory path: {UploadFolderPath}/{ArNumber}/{RequestGuid}
   * Used for bulk-deleting all files when the wizard is cancelled.
   */
  buildSessionDirPath(arNumber: string, requestGuid: string): string {
    const uploadFolderPath = envConfig.uploadFolderPath ?? 'DEV/UploadedFiles';
    return [uploadFolderPath, arNumber, requestGuid.toUpperCase()]
      .filter(segment => segment.length > 0)
      .join('/');
  }

  uploadFile(swiftPath: string, file: File): Observable<UploadProgress> {
    return this.repo.uploadFile(swiftPath, file).pipe(
      filter(
        event =>
          event.type === HttpEventType.UploadProgress ||
          event.type === HttpEventType.Response
      ),
      map(event => {
        if (event.type === HttpEventType.UploadProgress) {
          const progress = event as HttpUploadProgressEvent;
          const percent = progress.total
            ? Math.round((100 * progress.loaded) / progress.total)
            : 0;
          return { percent, done: false };
        }
        return { percent: 100, done: true } satisfies UploadProgress;
      })
    );
  }

  getFile(swiftPath: string): Observable<Blob> {
    return this.repo.getFile(swiftPath);
  }

  deleteFile(swiftPath: string): Observable<void> {
    return this.repo.deleteFile(swiftPath);
  }

  deleteDirectory(dirPath: string): Observable<void> {
    return this.repo.deleteDirectory(dirPath);
  }
}
